import { UseDropdownReturnValue, useModalState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useEffect, useMemo, useRef } from 'react'
import { Item, SellerOperator, SellerRestriction } from 'typings'
import { useFormStateContext } from '../../../../../providers'
import { SellerOption } from '../../Sellers'
import { useFeature } from 'hooks'

type Props = {
  name: string
}

export function useOptInStateControl(props: Props) {
  const { name } = props

  const { getFeature } = useFeature()

  const optinEnabled = getFeature('optin')

  const { form } = useFormStateContext()

  const seller: SellerRestriction | undefined = useWatch({
    form,
    name,
  })

  const sellerRestriction: SellerRestriction | undefined = useMemo(
    () =>
      !optinEnabled && seller?.operator == SellerOperator.OPTIN
        ? undefined
        : seller,
    [optinEnabled, seller]
  )

  const optInConfirmationModal = useModalState()

  const selectedOperatorRef = useRef<SellerOperator | undefined>(undefined)

  const changeOptIn = (selectedOperator: SellerOperator | undefined) => {
    if (selectedOperator === SellerOperator.ALL) form.setValue(name, undefined)

    form.setValue(name, {
      operator: selectedOperator,
      items: [] as Item[],
    })
  }

  const shouldAlertOptInChange = (
    selectedOperator: SellerOperator | undefined,
    previousOption: SellerRestriction | undefined
  ): boolean => {
    const previousItems = previousOption?.items ?? []

    return selectedOperator !== SellerOperator.OPTIN && previousItems.length > 0
  }

  const handleOptInSelection = (
    selectedValue: SellerOperator | undefined,
    previousOption: SellerRestriction | undefined
  ) => {
    if (selectedValue === previousOption?.operator) return

    if (shouldAlertOptInChange(selectedValue, previousOption)) {
      selectedOperatorRef.current = selectedValue
      optInConfirmationModal.show()
    } else {
      changeOptIn(selectedValue)
    }
  }

  const confirmOptInChange = () => {
    changeOptIn(selectedOperatorRef.current)
    selectedOperatorRef.current = undefined
  }

  const cancelOptInChange = (
    options: { id: SellerOperator; label: JSX.Element }[],
    state: UseDropdownReturnValue<SellerOption>
  ) => {
    const optInOption = options.find(
      (option) => option.id === SellerOperator.OPTIN
    )
    optInOption && state.selectItem(optInOption)
  }

  const isOptInSelection = (
    selectedValue: SellerOperator | undefined,
    previousOption: SellerRestriction | undefined
  ): boolean =>
    selectedValue === SellerOperator.OPTIN ||
    previousOption?.operator === SellerOperator.OPTIN

  const showOptInCounter: boolean = useMemo(
    () =>
      optinEnabled &&
      sellerRestriction?.operator === SellerOperator.OPTIN &&
      sellerRestriction?.items !== undefined &&
      sellerRestriction?.items.length > 0,
    [optinEnabled, sellerRestriction?.items, sellerRestriction?.operator]
  )

  useEffect(() => {
    if (!optinEnabled && sellerRestriction?.operator === SellerOperator.OPTIN) {
      form.setValue(name, undefined)
    }
  }, [form, name, optinEnabled, sellerRestriction?.operator])

  return {
    sellerRestriction,
    optinEnabled,
    handleOptInSelection,
    confirmOptInChange,
    cancelOptInChange,
    isOptInSelection,
    showOptInCounter,
    optInConfirmationModal,
  }
}
